import React from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Flex,
  Box,
  Text,
  Spinner,
  Badge,
  Link,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import {
  IEmpresaCard,
  useConferenciaAutmaisStatus,
  IAutmaisLogItem,
} from '../../../../hooks/useConferenciaFiscal';

interface Props {
  empresa: IEmpresaCard | null;
  range: { data_ini?: string; data_fim?: string };
  onClose: () => void;
}

// Estilo por status cru da API Autmais.
const ST: Record<string, { cor: string; bg: string; lab: string }> = {
  success: { cor: '#047857', bg: 'rgba(16,185,129,0.12)', lab: 'Sucesso' },
  warning: { cor: '#b45309', bg: 'rgba(245,158,11,0.15)', lab: 'Aviso' },
  error: { cor: '#b91c1c', bg: 'rgba(239,68,68,0.12)', lab: 'Erro' },
  processing: { cor: '#1d4ed8', bg: 'rgba(59,130,246,0.12)', lab: 'Processando' },
  to_process: { cor: '#64748B', bg: 'rgba(148,163,184,0.16)', lab: 'Na fila' },
  waiting_in_queue: { cor: '#64748B', bg: 'rgba(148,163,184,0.16)', lab: 'Aguardando' },
};
const stInfo = (s: string) => ST[s] || { cor: '#64748B', bg: 'rgba(148,163,184,0.16)', lab: s };

const fmtData = (d: string | null) => (d ? d.replace('T', ' ').slice(0, 16) : '—');

const Linha = ({ it }: { it: IAutmaisLogItem }) => {
  const s = stInfo(it.status);
  return (
    <Tr _hover={{ bg: 'rgba(27,136,245,0.035)' }}>
      <Td fontWeight={700} fontSize="12px">
        {it.tipo_nota || it.tipo_consulta}
      </Td>
      <Td>
        <Badge bg={s.bg} color={s.cor} textTransform="none" fontSize="10.5px" borderRadius="6px">
          {s.lab}
        </Badge>
      </Td>
      <Td fontSize="11px" color="#64748B" maxW="230px">
        {it.mensagem_erro || it.mensagem || '—'}
        {it.url_zip ? (
          <>
            {' · '}
            <Link href={it.url_zip} isExternal color="#1B88F5" fontWeight={600}>
              zip
            </Link>
          </>
        ) : (
          ''
        )}
      </Td>
      <Td fontSize="11px" color="#94A3B8" whiteSpace="nowrap">
        {fmtData(it.data_processamento)}
      </Td>
    </Tr>
  );
};

export const AutmaisLogDrawer = ({ empresa, range, onClose }: Props) => {
  const log = useConferenciaAutmaisStatus(empresa?.empresa_id ?? null, range);

  const renderCorpo = () => {
    if (log.isLoading) {
      return (
        <Flex justify="center" py={16}>
          <Spinner color="#b45309" />
        </Flex>
      );
    }
    if (log.isError) {
      return (
        <Text color="#b91c1c" py={8}>
          Não foi possível carregar o log do Autmais.
        </Text>
      );
    }
    if (!log.data?.itens.length) {
      return (
        <Text textAlign="center" color="#94A3B8" py={12}>
          Sem log de captura do Autmais neste período.
        </Text>
      );
    }
    const r = log.data.resumo;
    return (
      <>
        <Flex gap={4} mb={3} fontSize="12.5px" color="#475569" fontWeight={600} wrap="wrap">
          <Text>
            Tipos: <b>{r.total}</b>
          </Text>
          <Text color="#047857">
            OK: <b>{r.ok}</b>
          </Text>
          <Text color="#b45309">
            Aviso: <b>{r.aviso}</b>
          </Text>
          <Text color="#b91c1c">
            Erro: <b>{r.erro}</b>
          </Text>
          {r.processando > 0 && (
            <Text color="#1d4ed8">
              Processando: <b>{r.processando}</b>
            </Text>
          )}
        </Flex>
        <Box overflowX="auto" borderRadius="14px">
          <Table size="sm" minW="520px">
            <Thead>
              <Tr>
                <Th>Tipo</Th>
                <Th>Status</Th>
                <Th>Mensagem</Th>
                <Th>Processado</Th>
              </Tr>
            </Thead>
            <Tbody>
              {log.data.itens.map(it => (
                <Linha key={it.tipo_consulta} it={it} />
              ))}
            </Tbody>
          </Table>
        </Box>
      </>
    );
  };

  return (
    <Drawer isOpen={!!empresa} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent bg="rgba(255,255,255,0.9)" backdropFilter="blur(24px)">
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid rgba(15,23,42,0.06)">
          <Text fontSize="11px" color="#94A3B8" fontWeight={600}>
            ID {empresa?.external_id} · Captura Autmais
          </Text>
          <Text fontSize="17px" fontWeight={750} letterSpacing="-0.02em">
            {empresa?.razao_social}
          </Text>
          <Text fontSize="11px" color="#94A3B8" fontWeight={500} mt={0.5}>
            log do sistema que baixou as notas, por tipo de documento
          </Text>
        </DrawerHeader>
        <DrawerBody px={4} py={4}>
          {renderCorpo()}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
